import type { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CircleCheck, CircleDot, CircleDashed, FileClock, Info, OctagonAlert, PenLine, ShieldCheck, TriangleAlert, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { fill } from "../../lib/fill";
import { useKitCopy } from "../../lib/kit-copy";
import type { KitLabels } from "../../lib/kit-labels";

export type Severity = "exploited" | "high" | "medium" | "low" | "ok";

export const severityIcon: Record<Severity, LucideIcon> = {
  exploited: OctagonAlert,
  high: TriangleAlert,
  medium: CircleDot,
  low: Info,
  ok: CircleCheck,
};

export const chipVariants = cva("inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-chip px-2 text-2xs font-semibold", {
  variants: {
    tone: {
      exploited: "bg-exploited-bg text-exploited",
      high: "bg-high-bg text-high",
      medium: "bg-medium-bg text-medium",
      low: "bg-low-bg text-low",
      ok: "bg-ok-bg text-ok",
      neutral: "border border-border bg-surface-2 font-medium text-text-muted",
      brand: "bg-brand-50 text-brand-600",
    },
  },
  defaultVariants: { tone: "neutral" },
});

/** Severity / status chip. Always icon + label: colour is never the only signal. */
export function Chip({
  tone = "neutral",
  icon,
  children,
  className,
}: VariantProps<typeof chipVariants> & { icon?: LucideIcon | null; children: ReactNode; className?: string }) {
  const Icon = icon === null ? null : (icon ?? (tone && tone in severityIcon ? severityIcon[tone as Severity] : null));
  return (
    <span className={cn(chipVariants({ tone }), className)}>
      {Icon && <Icon aria-hidden className="size-3.5" strokeWidth={2} />}
      {children}
    </span>
  );
}

/** The English severity words. The kit's chips take theirs from `KitCopyProvider` (`severityLabelKey`). */
export const severityLabel: Record<Severity, string> = {
  exploited: "Actively exploited",
  high: "High",
  medium: "Medium",
  low: "Low",
  ok: "Not affected",
};
/** Each severity's word as a `KitLabels` name (`labels[severityLabelKey.high]`). */
export const severityLabelKey: Record<Severity, keyof KitLabels> = { exploited: "sevExploited", high: "sevHigh", medium: "sevMedium", low: "sevLow", ok: "notAffected" };
export function SeverityChip({ severity, label }: { severity: Severity; label?: string }) {
  const l = useKitCopy().labels;
  return <Chip tone={severity}>{label ?? l[severityLabelKey[severity]]}</Chip>;
}

export type CaseStatus = "draft" | "ready" | "submitted" | "overdue" | "closed";
export type FindingStatus = "new" | "triage" | "affected" | "not_affected" | "fixed";
/**
 * The API's document statuses (B15: not_started, draft, rendered = "Needs review", approved = "Reviewed", signed),
 * plus `needs_signature` and `expiring` (a reviewed declaration waiting for a signature, a security.txt near its
 * Expires date) for summaries.
 */
export type DocumentStatus = "not_started" | "draft" | "rendered" | "approved" | "signed" | "needs_signature" | "expiring";

/** Tone, the word (a `KitLabels` name) and the icon of a status. */
type StatusLook = [Severity | "neutral" | "brand", keyof KitLabels, LucideIcon?];
const caseStatus: Record<CaseStatus, StatusLook> = {
  draft: ["neutral", "statusDraft", CircleDashed],
  ready: ["brand", "readyToSend", CircleCheck],
  submitted: ["ok", "statusSubmitted"],
  overdue: ["exploited", "statusOverdue"],
  closed: ["neutral", "statusClosed", CircleCheck],
};
const findingStatus: Record<FindingStatus, StatusLook> = {
  new: ["brand", "statusNew", CircleDot],
  triage: ["medium", "needsTriage"],
  affected: ["high", "statusAffected"],
  not_affected: ["ok", "notAffected"],
  fixed: ["ok", "statusFixed"],
};
const documentStatus: Record<DocumentStatus, StatusLook> = {
  not_started: ["neutral", "notStarted", CircleDashed],
  draft: ["brand", "statusDraft", PenLine],
  rendered: ["medium", "needsReview", FileClock],
  approved: ["ok", "statusReviewed", CircleCheck],
  signed: ["ok", "statusSigned", ShieldCheck],
  needs_signature: ["medium", "needsSignature", PenLine],
  expiring: ["high", "expiringSoon"],
};
/** Status chip with the status's tone and icon. The word comes from `KitCopyProvider`; `label` replaces it. */
export function StatusChip(props: ({ kind: "case"; status: CaseStatus } | { kind: "finding"; status: FindingStatus } | { kind: "document"; status: DocumentStatus }) & { label?: string }) {
  const l = useKitCopy().labels;
  const map = (props.kind === "case" ? caseStatus : props.kind === "finding" ? findingStatus : documentStatus) as Record<string, StatusLook>;
  const [tone, word, icon] = map[props.status]!;
  return (
    <Chip tone={tone} icon={icon}>
      {props.label ?? l[word]}
    </Chip>
  );
}

/** Plan badge: the plan's name (a product name, kept as is) or, in the trial, the days left (`trialDaysLeft` label). */
export function PlanBadge({ plan, trialDaysLeft }: { plan: "Starter" | "Professional" | "Business" | "Trial"; trialDaysLeft?: number }) {
  const l = useKitCopy().labels;
  return (
    <Chip tone="brand" icon={null}>
      {plan === "Trial" ? fill(l.trialDaysLeft, { trialDaysLeft: trialDaysLeft ?? 0 }) : plan}
    </Chip>
  );
}

export type Confidence = "high" | "medium" | "low";
const toneForConfidence: Record<Confidence, "ok" | "medium" | "neutral"> = { high: "ok", medium: "medium", low: "neutral" };
const confidenceWord: Record<Confidence, keyof KitLabels> = { high: "confidenceHigh", medium: "confidenceMedium", low: "confidenceLow" };
export function ConfidenceBadge({ level }: { level: Confidence }) {
  const l = useKitCopy().labels;
  return (
    <Chip tone={toneForConfidence[level]} icon={level === "low" ? CircleDashed : undefined}>
      {l[confidenceWord[level]]}
    </Chip>
  );
}
